/**
 * usePatientFieldNormalization Hook
 *
 * Handles automatic field normalization and character conversion
 * Matches RA2's culum_control() JavaScript function behavior
 */

import { useCallback } from "react";
import { UseFormWatch, UseFormSetValue } from "react-hook-form";
import {
  PATIENT_ADDRESS_KANA_MAX_LENGTH,
  PatientFormSchemaType,
} from "@/pages/main/patient/types";
import {
  normalizeNameKana,
  normalizePhoneNumber,
  normalizePostalCode,
  normalizeInsurerNumber,
  normalizeAddress,
  normalizeCardNumber,
  normalizeHalfWidthCodeField,
} from "@/utils/japaneseText";

export interface UsePatientFieldNormalizationReturn {
  // Patient information field handlers
  normalizePatientName: () => void;
  normalizePatientNameKana: () => void;
  normalizePatientPostalCode1: () => void;
  normalizePatientPostalCode2: () => void;
  normalizePatientTelephone: () => void;
  normalizePatientAddress1: () => void;
  normalizePatientAddress2: () => void;
  normalizePatientAddress3: () => void;
  normalizePatientAddressName: () => void;
  normalizePatientAddressKana: () => void;

  // Health insurance card field handlers
  normalizeInsurerNumber: () => void;
  normalizeCardMark: () => void;
  normalizeCardNumber: () => void;
  normalizeInsuredName: () => void;
  normalizeInsuredNameKana: () => void;
  normalizeInsuredPostalCode1: () => void;
  normalizeInsuredPostalCode2: () => void;
  normalizeInsuredTelephone: () => void;
  normalizeInsuredAddress1: () => void;
  normalizeInsuredAddress2: () => void;
  normalizeInsuredAddressKana: () => void;

  // Combined normalization for blur events
  createBlurHandler: (
    fieldPath: keyof PatientFormSchemaType | string,
  ) => () => void;
}

/**
 * Hook for automatic field normalization on patient edit form
 */
export function usePatientFieldNormalization(
  watch: UseFormWatch<PatientFormSchemaType>,
  setValue: UseFormSetValue<PatientFormSchemaType>,
): UsePatientFieldNormalizationReturn {
  // ==================== Patient Information Normalizers ====================

  const normalizePatientName = useCallback(() => {
    // Names don't get normalized, but we sync to insurance if relationship = 本人
    // This is handled by useSyncPatientToInsurance
  }, []);

  /** No-op: 受診者 氏名カナは PatientInfoSection の useJapaneseNameKanaFields が処理する */
  const normalizePatientNameKana = useCallback(() => {}, []);

  const normalizePatientPostalCode1 = useCallback(() => {
    const value = watch("patient_information.postal_code1");
    if (value) {
      const normalized = normalizePostalCode(value);
      if (normalized !== value) {
        setValue("patient_information.postal_code1", normalized);
      }

      // Sync to insurance card if relationship = 本人
      const relationshipId = watch("health_insurance_card.relationship_id");
      if (relationshipId === 1 || relationshipId === 3) {
        setValue("health_insurance_card.postal_code1", normalized);
      }
    }
  }, [watch, setValue]);

  const normalizePatientPostalCode2 = useCallback(() => {
    const value = watch("patient_information.postal_code2");
    if (value) {
      const normalized = normalizePostalCode(value);
      if (normalized !== value) {
        setValue("patient_information.postal_code2", normalized);
      }

      // Sync to insurance card if relationship = 本人
      const relationshipId = watch("health_insurance_card.relationship_id");
      if (relationshipId === 1 || relationshipId === 3) {
        setValue("health_insurance_card.postal_code2", normalized);
      }
    }
  }, [watch, setValue]);

  const normalizePatientTelephone = useCallback(() => {
    const value = watch("patient_information.telephone");
    if (value) {
      const normalized = normalizePhoneNumber(value);
      if (normalized !== value) {
        setValue("patient_information.telephone", normalized);
      }

      // Sync to insurance card if relationship = 本人
      const relationshipId = watch("health_insurance_card.relationship_id");
      if (relationshipId === 1 || relationshipId === 3) {
        setValue("health_insurance_card.telephone", normalized);
      }
    }
  }, [watch, setValue]);

  const normalizePatientAddress1 = useCallback(() => {
    const value = watch("patient_information.address1");
    if (value) {
      const normalized = normalizeAddress(value);
      if (normalized !== value) {
        setValue("patient_information.address1", normalized);
      }

      // Sync to insurance card if relationship = 本人
      const relationshipId = watch("health_insurance_card.relationship_id");
      if (relationshipId === 1 || relationshipId === 3) {
        setValue("health_insurance_card.address1", normalized);
      }
    } else {
      // Clear address kana if address1 is empty (RA2: No900)
      setValue("patient_information.address_kana", "");
      const relationshipId = watch("health_insurance_card.relationship_id");
      if (relationshipId === 1 || relationshipId === 3) {
        setValue("health_insurance_card.address_kana", "");
      }
    }
  }, [watch, setValue]);

  const normalizePatientAddress2 = useCallback(() => {
    const value = watch("patient_information.address2");
    if (value) {
      const normalized = normalizeAddress(value);
      if (normalized !== value) {
        setValue("patient_information.address2", normalized);
      }

      // Update insurance card address2 (combined address2+address3+address_name)
      const relationshipId = watch("health_insurance_card.relationship_id");
      if (relationshipId === 1 || relationshipId === 3) {
        const address2 = normalized;
        const address3 = watch("patient_information.address3") || "";
        const addressName = watch("patient_information.address_name") || "";
        const combined = [address2, address3, addressName]
          .filter(Boolean)
          .join(" ");
        setValue("health_insurance_card.address2", combined);
      }
    }
  }, [watch, setValue]);

  const normalizePatientAddress3 = useCallback(() => {
    const value = watch("patient_information.address3");
    if (value) {
      const normalized = normalizeAddress(value);
      if (normalized !== value) {
        setValue("patient_information.address3", normalized);
      }

      // Update insurance card address2 (combined)
      const relationshipId = watch("health_insurance_card.relationship_id");
      if (relationshipId === 1 || relationshipId === 3) {
        const address2 = watch("patient_information.address2") || "";
        const address3 = normalized;
        const addressName = watch("patient_information.address_name") || "";
        const combined = [address2, address3, addressName]
          .filter(Boolean)
          .join(" ");
        setValue("health_insurance_card.address2", combined);
      }
    }
  }, [watch, setValue]);

  const normalizePatientAddressName = useCallback(() => {
    const value = watch("patient_information.address_name");
    if (value) {
      const normalized = normalizeAddress(value);
      if (normalized !== value) {
        setValue("patient_information.address_name", normalized);
      }

      // Update insurance card address2 (combined)
      const relationshipId = watch("health_insurance_card.relationship_id");
      if (relationshipId === 1 || relationshipId === 3) {
        const address2 = watch("patient_information.address2") || "";
        const address3 = watch("patient_information.address3") || "";
        const addressName = normalized;
        const combined = [address2, address3, addressName]
          .filter(Boolean)
          .join(" ");
        setValue("health_insurance_card.address2", combined);
      }
    }
  }, [watch, setValue]);

  const normalizePatientAddressKana = useCallback(() => {
    const value = watch("patient_information.address_kana");
    if (value) {
      const normalized = value.slice(0, PATIENT_ADDRESS_KANA_MAX_LENGTH);
      if (normalized !== value) {
        setValue("patient_information.address_kana", normalized);
      }

      // Sync to insurance card if relationship = 本人
      const relationshipId = watch("health_insurance_card.relationship_id");
      if (relationshipId === 1 || relationshipId === 3) {
        setValue("health_insurance_card.address_kana", normalized);
      }
    }
  }, [watch, setValue]);

  // ==================== Health Insurance Card Normalizers ====================

  const normalizeInsurerNumberHandler = useCallback(() => {
    const value = watch("health_insurance_card.insurer_number");
    if (value) {
      const normalized = normalizeInsurerNumber(value);
      if (normalized !== value) {
        setValue("health_insurance_card.insurer_number", normalized);
      }
    }
  }, [watch, setValue]);

  const normalizeCardMark = useCallback(() => {
    const value = watch("health_insurance_card.card_mark");
    if (value) {
      const normalized = normalizeHalfWidthCodeField(value);
      if (normalized !== value) {
        setValue("health_insurance_card.card_mark", normalized);
      }
    }
  }, [watch, setValue]);

  const normalizeCardNumberHandler = useCallback(() => {
    const value = watch("health_insurance_card.card_number");
    if (value) {
      const normalized = normalizeCardNumber(value);
      if (normalized !== value) {
        setValue("health_insurance_card.card_number", normalized);
      }
    }
  }, [watch, setValue]);

  const normalizeInsuredName = useCallback(() => {
    // Insurance name doesn't get auto-normalized
    // It's synced from patient info when relationship = 本人
  }, []);

  const normalizeInsuredNameKana = useCallback(() => {
    const value = watch("health_insurance_card.name_kana");
    if (value) {
      const normalized = normalizeNameKana(value);
      if (normalized !== value) {
        setValue("health_insurance_card.name_kana", normalized);
      }
    }
  }, [watch, setValue]);

  const normalizeInsuredPostalCode1 = useCallback(() => {
    const value = watch("health_insurance_card.postal_code1");
    if (value) {
      const normalized = normalizePostalCode(value);
      if (normalized !== value) {
        setValue("health_insurance_card.postal_code1", normalized);
      }
    }
  }, [watch, setValue]);

  const normalizeInsuredPostalCode2 = useCallback(() => {
    const value = watch("health_insurance_card.postal_code2");
    if (value) {
      const normalized = normalizePostalCode(value);
      if (normalized !== value) {
        setValue("health_insurance_card.postal_code2", normalized);
      }
    }
  }, [watch, setValue]);

  const normalizeInsuredTelephone = useCallback(() => {
    const value = watch("health_insurance_card.telephone");
    if (value) {
      const normalized = normalizePhoneNumber(value);
      if (normalized !== value) {
        setValue("health_insurance_card.telephone", normalized);
      }
    }
  }, [watch, setValue]);

  const normalizeInsuredAddress1 = useCallback(() => {
    const value = watch("health_insurance_card.address1");
    if (value) {
      const normalized = normalizeAddress(value);
      if (normalized !== value) {
        setValue("health_insurance_card.address1", normalized);
      }
    }
  }, [watch, setValue]);

  const normalizeInsuredAddress2 = useCallback(() => {
    const value = watch("health_insurance_card.address2");
    if (value) {
      const normalized = normalizeAddress(value);
      if (normalized !== value) {
        setValue("health_insurance_card.address2", normalized);
      }
    }
  }, [watch, setValue]);

  const normalizeInsuredAddressKana = useCallback(() => {
    const value = watch("health_insurance_card.address_kana");
    if (value) {
      const normalized = value.slice(0, PATIENT_ADDRESS_KANA_MAX_LENGTH);
      if (normalized !== value) {
        setValue("health_insurance_card.address_kana", normalized);
      }
    }
  }, [watch, setValue]);

  // ==================== Blur Handler Factory ====================

  const createBlurHandler = useCallback(
    (fieldPath: string) => {
      const handlers: Record<string, () => void> = {
        "patient_information.name": normalizePatientName,
        "patient_information.name_kana": normalizePatientNameKana,
        "patient_information.postal_code1": normalizePatientPostalCode1,
        "patient_information.postal_code2": normalizePatientPostalCode2,
        "patient_information.telephone": normalizePatientTelephone,
        "patient_information.address1": normalizePatientAddress1,
        "patient_information.address2": normalizePatientAddress2,
        "patient_information.address3": normalizePatientAddress3,
        "patient_information.address_name": normalizePatientAddressName,
        "patient_information.address_kana": normalizePatientAddressKana,
        "health_insurance_card.insurer_number": normalizeInsurerNumberHandler,
        "health_insurance_card.card_mark": normalizeCardMark,
        "health_insurance_card.card_number": normalizeCardNumberHandler,
        "health_insurance_card.name": normalizeInsuredName,
        "health_insurance_card.name_kana": normalizeInsuredNameKana,
        "health_insurance_card.postal_code1": normalizeInsuredPostalCode1,
        "health_insurance_card.postal_code2": normalizeInsuredPostalCode2,
        "health_insurance_card.telephone": normalizeInsuredTelephone,
        "health_insurance_card.address1": normalizeInsuredAddress1,
        "health_insurance_card.address2": normalizeInsuredAddress2,
        "health_insurance_card.address_kana": normalizeInsuredAddressKana,
      };

      return handlers[fieldPath] || (() => {});
    },
    [
      normalizePatientName,
      normalizePatientNameKana,
      normalizePatientPostalCode1,
      normalizePatientPostalCode2,
      normalizePatientTelephone,
      normalizePatientAddress1,
      normalizePatientAddress2,
      normalizePatientAddress3,
      normalizePatientAddressName,
      normalizePatientAddressKana,
      normalizeInsurerNumberHandler,
      normalizeCardMark,
      normalizeCardNumberHandler,
      normalizeInsuredName,
      normalizeInsuredNameKana,
      normalizeInsuredPostalCode1,
      normalizeInsuredPostalCode2,
      normalizeInsuredTelephone,
      normalizeInsuredAddress1,
      normalizeInsuredAddress2,
      normalizeInsuredAddressKana,
    ],
  );

  return {
    normalizePatientName,
    normalizePatientNameKana,
    normalizePatientPostalCode1,
    normalizePatientPostalCode2,
    normalizePatientTelephone,
    normalizePatientAddress1,
    normalizePatientAddress2,
    normalizePatientAddress3,
    normalizePatientAddressName,
    normalizePatientAddressKana,
    normalizeInsurerNumber: normalizeInsurerNumberHandler,
    normalizeCardMark,
    normalizeCardNumber: normalizeCardNumberHandler,
    normalizeInsuredName,
    normalizeInsuredNameKana,
    normalizeInsuredPostalCode1,
    normalizeInsuredPostalCode2,
    normalizeInsuredTelephone,
    normalizeInsuredAddress1,
    normalizeInsuredAddress2,
    normalizeInsuredAddressKana,
    createBlurHandler,
  };
}

export default usePatientFieldNormalization;
